import type { ReactNode } from "react";

import { BuildingIcon, RepeatIcon } from "./icons";

/** One segment of the optional mode switch under the location body (e.g. Asiel / Pension). */
export interface LocationSelectorMode {
  /** Stable key reported back through {@link LocationSelectorProps.onModeChange}. */
  value: string;
  /** Visible segment label. */
  label: string;
  /** Optional glyph in front of the label. */
  icon?: ReactNode;
  /** Renders the segment inert (e.g. the module is switched off for this branch). */
  disabled?: boolean;
}

export interface LocationSelectorProps {
  /** Small uppercase eyebrow label (e.g. "VESTIGING"). */
  label: string;
  /** The active location name (bold). */
  name: string;
  /** Secondary line under the name (e.g. the town). */
  place?: string;
  /** Label for the switch action (e.g. "Wisselen"). */
  switchLabel: string;
  /** Fired when the switch action is pressed. Omit/disabled renders the card read-only. */
  onSwitch?: () => void;
  /** Read-only stub mode (backend not migrated) — the switch action is inert. */
  disabled?: boolean;
  /** Optional segmented mode switch under the location (needs 2+ entries to render). */
  modes?: LocationSelectorMode[];
  /** The selected {@link LocationSelectorMode.value}. */
  activeMode?: string;
  /** Fired with the picked mode value; omit to render the segments inert. */
  onModeChange?: (value: string) => void;
  /** Accessible name for the mode switch group (e.g. "Werkmodus"). */
  modesLabel?: string;
}

/**
 * jve-2-6: the sidebar location (vestiging) selector card — an eyebrow label + a "Wisselen" pill in
 * the header row, then a green rounded tile (building glyph) beside the active location + town.
 * An EXACT copy of the live Jmix sidebar's `.sb-vest-card` (frontend-react/sidebar) so the React SPA
 * reproduces what a Jmix user sees. Presentational + extraction-ready; the host wires data + the
 * switch handler (rendered as a read-only stub until the backend is migrated).
 *
 * `modes` additively appends a segmented switch under the location (Asiel / Pension) — purely
 * optional, so every existing consumer renders unchanged.
 */
export function LocationSelector({
  label,
  name,
  place,
  switchLabel,
  onSwitch,
  disabled,
  modes,
  activeMode,
  onModeChange,
  modesLabel,
}: LocationSelectorProps) {
  const showModes = (modes?.length ?? 0) > 1;
  return (
    <div className="fui-location" data-disabled={disabled ? "" : undefined}>
      <div className="fui-location__header">
        <span className="fui-location__label">{label}</span>
        <button
          type="button"
          className="fui-location__switch"
          onClick={onSwitch}
          disabled={disabled || !onSwitch}
          aria-label={switchLabel}
          title={switchLabel}
        >
          <RepeatIcon width={14} height={14} />
          <span className="fui-location__switch-label">{switchLabel}</span>
        </button>
      </div>
      <div className="fui-location__body">
        <span className="fui-location__tile" aria-hidden="true">
          <BuildingIcon width={18} height={18} />
        </span>
        <div className="fui-location__text">
          <span className="fui-location__name" title={name}>
            {name}
          </span>
          {place && <span className="fui-location__place">{place}</span>}
        </div>
      </div>
      {showModes && (
        <div className="fui-location__modes" role="group" aria-label={modesLabel ?? label}>
          {modes!.map((mode) => {
            const active = mode.value === activeMode;
            return (
              <button
                key={mode.value}
                type="button"
                className="fui-location__mode"
                data-active={active ? "" : undefined}
                aria-pressed={active}
                title={mode.label}
                disabled={disabled || mode.disabled || !onModeChange}
                onClick={() => onModeChange?.(mode.value)}
              >
                {mode.icon && (
                  <span className="fui-location__mode-icon" aria-hidden="true">
                    {mode.icon}
                  </span>
                )}
                <span className="fui-location__mode-label">{mode.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
